/**
 * SandboxUserPicker — the sandbox "act as a user" roster list, shown in the
 * "Switch user" popover on the acting-as banner (sandbox mode). Mirrors
 * PersonaPicker (demo): same banner pattern, different source — the picked
 * sandbox org's real roster, bound to CurrentUser via the production-identity
 * mapping so sandbox rehearses production.
 *
 * Extracted from the old standalone SandboxUserSwitcher pill so sandbox uses the
 * same header + banner pattern as demo.
 */
import { useQuery } from '@tanstack/react-query'
import { Check } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAppStore } from '@/store/appStore'
import { useTimesheetStore } from '@/modules/timesheet/stores/timesheetStore'
import { rosterRowToCurrentUser, type RosterIdentity } from '@/lib/productionIdentity'
import { cn } from '@/lib/utils'

type DbUserRow = {
  id: string; name: string; email: string | null; initials: string | null
  title: string | null; system_role: string | null; department: string | null
  cost_center_code: string | null; clearance: string | null
  manager_id: string | null; primary_organization_id: string | null
}

function toRosterIdentity(r: DbUserRow): RosterIdentity {
  return {
    id: r.id, name: r.name, email: r.email, initials: r.initials, title: r.title,
    systemRole: r.system_role, department: r.department, costCenterCode: r.cost_center_code,
    clearance: r.clearance, managerId: r.manager_id, primaryOrganizationId: r.primary_organization_id,
  }
}

export function SandboxUserPicker({ onPicked }: { onPicked?: () => void }) {
  const appMode = useAppStore((s) => s.appMode)
  const activeOrganizationId = useAppStore((s) => s.activeOrganizationId)
  const currentUser = useAppStore((s) => s.currentUser)

  // The picked sandbox org's roster — the people you can act as. Empty until the
  // org has users (a fresh sandbox starts empty, per the productization pipeline).
  const { data: roster = [] } = useQuery({
    queryKey: ['sandbox-roster', activeOrganizationId],
    queryFn: async (): Promise<RosterIdentity[]> => {
      const { data, error } = await supabase
        .from('users')
        .select('id, name, email, initials, title, system_role, department, cost_center_code, clearance, manager_id, primary_organization_id')
        .eq('primary_organization_id', activeOrganizationId as string)
        .order('name')
      if (error) throw error
      return (data as DbUserRow[]).map(toRosterIdentity)
    },
    enabled: appMode === 'sandbox' && !!activeOrganizationId,
    staleTime: 60_000,
  })

  // Also let you act as anyone who is a member of a People Group, regardless of
  // org — so the workflow teams are switchable even before org assignment.
  const { data: groupRoster = [] } = useQuery({
    queryKey: ['sandbox-group-roster'],
    queryFn: async (): Promise<RosterIdentity[]> => {
      const { data: groups } = await supabase.from('people_groups').select('members')
      const names = [...new Set((groups ?? [])
        .flatMap((g) => (Array.isArray((g as { members?: unknown[] }).members) ? (g as { members: unknown[] }).members : []))
        .map((m) => String(m)))].filter(Boolean)
      if (names.length === 0) return []
      const { data, error } = await supabase
        .from('users')
        .select('id, name, email, initials, title, system_role, department, cost_center_code, clearance, manager_id, primary_organization_id')
        .in('name', names)
        .order('name')
      if (error) throw error
      return (data as DbUserRow[]).map(toRosterIdentity)
    },
    enabled: appMode === 'sandbox',
    staleTime: 60_000,
  })

  const merged = [...new Map([...roster, ...groupRoster].map((r) => [r.id, r])).values()]
    .sort((a, b) => a.name.localeCompare(b.name))

  if (appMode !== 'sandbox') return null

  function pick(identity: RosterIdentity) {
    useAppStore.getState().setCurrentUser(rosterRowToCurrentUser(identity))
    // Rebind the timesheet identity too — without this the dashboard and
    // My Timesheet keep wearing the PREVIOUS persona (live walk 2026-07-26:
    // sandbox acting-as Jennifer Muller still greeted "Good evening, Renee"
    // with Renee's week). Mirrors demo's applyPersona.
    useTimesheetStore.getState().setCurrentEmployeeId(identity.id)
    // Clear any demo-mode role override; sandbox derives role from the real
    // user row (rosterRowToCurrentUser above), not a persona override.
    useAppStore.getState().setTimesheetRoleOverride(null)
    onPicked?.()
  }

  if (merged.length === 0) {
    if (!activeOrganizationId) {
      return <div className="px-3 py-4 text-[12px] text-text-tertiary">Choose a sandbox org, or add People Groups, to load people.</div>
    }
    return <div className="px-3 py-4 text-[12px] text-text-tertiary">No users in this org yet — create one, then switch to it here.</div>
  }

  return (
    <div className="max-h-[44vh] overflow-y-auto py-1">
      {merged.map((r) => (
        <button
          key={r.id}
          onClick={() => pick(r)}
          className={cn(
            'w-full text-left px-3 py-2 flex items-center gap-2.5 transition-colors',
            r.id === currentUser.id ? 'bg-brand-50' : 'hover:bg-surface-muted',
          )}
        >
          <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700 text-[11px] font-semibold">
            {r.initials || r.name.slice(0, 2).toUpperCase()}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-body-sm font-medium text-text-primary truncate">{r.name}</span>
            <span className="block text-[10px] text-text-tertiary truncate leading-snug">
              {[r.title, r.systemRole].filter(Boolean).join(' · ') || 'User'}
            </span>
          </span>
          {r.id === currentUser.id && <Check size={14} className="text-brand-600 flex-shrink-0" />}
        </button>
      ))}
    </div>
  )
}
